import { ReactNode, useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: ReactNode;
  variant?: 'fade-up' | 'fly';
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  className?: string;
  once?: boolean;
}

const DISTANCE: Record<string, number> = { up: 44, down: -44, left: 64, right: -64 };
const SCALE_START = { 'fade-up': 1, fly: 0.9 };

export default function Reveal({
  children,
  variant = 'fade-up',
  direction = 'up',
  delay = 0,
  className = '',
  once = true,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const dist = DISTANCE[direction];
  const scaleStart = SCALE_START[variant];
  const duration = variant === 'fly' ? 650 : 750;
  const easing = variant === 'fly' ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.22, 1, 0.36, 1)';

  const x = direction === 'left' || direction === 'right' ? dist : 0;
  const y = direction === 'up' || direction === 'down' ? dist : 0;

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible
      ? 'translate(0, 0) scale(1)'
      : `translate(${x}px, ${y}px) scale(${scaleStart})`,
    transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
    transitionDelay: `${delay}ms`,
    willChange: 'opacity, transform',
  };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}